export type PageTab = 'tasks' | 'generation' | 'report';

export interface TabDef {
  id: PageTab;
  label: string;
}

export const TAB_QUERY_KEY = 'tab';

export const DEFAULT_TAB: PageTab = 'tasks';

const ALL_TABS: TabDef[] = [
  { id: 'tasks', label: 'Tasks' },
  { id: 'generation', label: 'Task generation' },
  { id: 'report', label: 'Instant task report' }
];

// The report tab is SUPER_USER-only, so it is absent (not disabled) otherwise.
export const tabList = (canViewReport: boolean): TabDef[] =>
  ALL_TABS.filter((t) => t.id !== 'report' || canViewReport);

const toParams = (search: string | URLSearchParams): URLSearchParams =>
  typeof search === 'string' ? new URLSearchParams(search) : new URLSearchParams(search.toString());

// Unknown or unauthorised values fall back to the default rather than erroring.
export const resolveTab = (search: string | URLSearchParams, canViewReport: boolean): PageTab => {
  const raw = toParams(search).get(TAB_QUERY_KEY);
  const hit = tabList(canViewReport).find((t) => t.id === raw);
  return hit ? hit.id : DEFAULT_TAB;
};

// Keeps every other query param; the default tab is written as no param.
export const searchWithTab = (search: string | URLSearchParams, tab: PageTab): string => {
  const params = toParams(search);
  if (tab === DEFAULT_TAB) params.delete(TAB_QUERY_KEY);
  else params.set(TAB_QUERY_KEY, tab);
  const out = params.toString();
  return out ? `?${out}` : '';
};

// Roving-tabindex keyboard model: arrows wrap, Home/End jump. Null = not a nav key.
export const tabForKey = (tabs: TabDef[], current: PageTab, key: string): PageTab | null => {
  if (!tabs.length) return null;
  const idx = Math.max(
    0,
    tabs.findIndex((t) => t.id === current)
  );
  switch (key) {
    case 'ArrowRight':
      return tabs[(idx + 1) % tabs.length].id;
    case 'ArrowLeft':
      return tabs[(idx - 1 + tabs.length) % tabs.length].id;
    case 'Home':
      return tabs[0].id;
    case 'End':
      return tabs[tabs.length - 1].id;
    default:
      return null;
  }
};
